import { IAppContext } from 'App/AppContext';
import { saveOutputLocationBinding } from 'components/OutputLocations/outputLocationActions';
import { DocumentOutputPurpose } from 'models/documentOutputLocation';
import ResourceLink from 'models/resourceLink';
import ResourceList from 'models/resourceList';
import { apiCreateLink, apiDeleteLink } from 'services/Api/linkService';
import { apiRequest, GraphScopeId } from 'services/Auth/authConfig';
import Config from 'services/Config/configService';
import { graphUploadFileToFolder } from 'services/Graph/graphServiceDrive';
import AppError from 'utils/appError';

export const defaultReportTemplateFileName = 'ISOPlanner.Report.Template.docx';

//
// Upload the default Word template to the category of the general reports and bind it as their template.
// The previous template link and any link with the same name are removed on a best effort basis.
// Returns the new template link.
//
export const uploadDefaultReportTemplate = async (
  appContext: IAppContext,
  reportList: ResourceList,
  listLinks: ResourceLink[],
  oldTemplateLinkId: number | undefined,
): Promise<ResourceLink> => {
  if (!reportList.spDriveId || !reportList.spDriveItemId) {
    throw new AppError('uploadDefaultReportTemplate: The report list has no drive or drive item Id');
  }

  //download template
  const url = Config.getAppURL() + `/templates/${defaultReportTemplateFileName}`;
  const blob = await fetch(url).then((r) => r.blob());
  const file = new File([blob], defaultReportTemplateFileName);

  //upload to SharePoint
  const graphInterface = await appContext.getGraphInterface(GraphScopeId.Library);
  const template = await graphUploadFileToFolder(
    graphInterface.client,
    file,
    reportList.spDriveId,
    reportList.spDriveItemId,
    defaultReportTemplateFileName,
  );

  //create item in library
  if (!template || !template.id || !template.webUrl) {
    throw new AppError('uploadDefaultReportTemplate: Item Id has no parent reference or web url');
  }

  //first try to delete the old template link
  const accessToken = await appContext.getAccessToken(apiRequest.scopes);
  if (oldTemplateLinkId) {
    try {
      await apiDeleteLink(oldTemplateLinkId, accessToken);
    } catch {
      //ignore
    }
  }

  //create new template link
  let newLink = new ResourceLink();
  newLink.linkName = template?.name ?? defaultReportTemplateFileName;
  newLink.driveItemId = template.id;
  newLink.linkURL = template.webUrl;
  newLink.listId = reportList.listId;

  //delete any link with the same name
  const existingLink = listLinks.find((l) => l.linkName.toLowerCase() === newLink.linkName.toLowerCase());
  if (existingLink) {
    try {
      await apiDeleteLink(existingLink.linkId, accessToken);
    } catch {
      //ignore
    }
  }

  newLink = await apiCreateLink(newLink, accessToken, appContext.globalDataCache);

  await saveOutputLocationBinding(appContext, DocumentOutputPurpose.GeneralReports, reportList.listId, newLink.linkId);

  return newLink;
};
